/**
 * Offline cache coherence for multi-rack (#995).
 * Keeps request.rackConfigurations aligned with rack API / React Query.
 */

import { offlineCache, type OfflineRackOperation } from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupRacksByStatus } from '../components/racks/rackListUtils';

import type {
  GroupedRackConfigurations,
  RackConfiguration,
} from '../types/rack';

/** Rack list from cached request detail (offline reads). */
export async function getCachedRackList(
  requestId: string,
): Promise<RackConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.rackConfigurations ?? [];
}

/** Grouped racks derived from cached list (same shape as GET …/racks/grouped). */
export async function getCachedRackGrouped(
  requestId: string,
): Promise<GroupedRackConfigurations> {
  return groupRacksByStatus(await getCachedRackList(requestId));
}

/** Single rack from cached list when offline. */
export async function getCachedRackById(
  requestId: string,
  rackId: number,
): Promise<RackConfiguration | undefined> {
  return (await getCachedRackList(requestId)).find(rack => rack.id === rackId);
}

/**
 * After a successful online rack fetch or mutation refetch, mirror racks onto the
 * cached request so offline reads stay consistent.
 */
export async function mirrorRacksToOfflineRequestCache(
  requestId: string,
  racks: RackConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestRacks(requestId, racks);
}

/** Pending offline rack queue entries for one request (create/update/delete/reorder). */
export async function getPendingRackOpsForRequest(
  requestId: string,
): Promise<Awaited<ReturnType<typeof offlineCache.getPendingRackOperations>>> {
  const pending = await offlineCache.getPendingRackOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline rack ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightRackOpsForRequest(
  requestId: string,
): Promise<OfflineRackOperation[]> {
  const queue = await offlineCache.getOfflineRackQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline rack operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic rack already
 * written into the base list (when the request detail was cached) is not
 * duplicated. (#995 bug 6)
 */
export function applyPendingRackOperations(
  requestId: string,
  base: RackConfiguration[],
  ops: OfflineRackOperation[],
): RackConfiguration[] {
  // Story 3.3: route Racks through the shared descriptor-engine overlay. The
  // `statusField: 'rackStatus'` option keeps the synthesized-create shape
  // byte-identical to the pre-engine bespoke overlay (Racks names its existing/
  // new column `rackStatus`, not `status`) — parity oracle
  // `racks-offline-overlay.json`.
  return applyPendingRepeatableOperations<
    RackConfiguration,
    OfflineRackOperation
  >(
    requestId,
    base,
    ops,
    {
      instanceId: op => op.rackId,
      reorderIds: op => op.rackIds,
    },
    { statusField: 'rackStatus' },
  );
}

/** Cached rack list with pending offline operations overlaid (offline reads). */
export async function getOfflineRackList(
  requestId: string,
): Promise<RackConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedRackList(requestId),
    getPendingRackOpsForRequest(requestId),
  ]);
  return applyPendingRackOperations(requestId, base, ops);
}

/** Grouped racks (existing/new) from the overlaid offline list. */
export async function getOfflineRackGrouped(
  requestId: string,
): Promise<GroupedRackConfigurations> {
  return groupRacksByStatus(await getOfflineRackList(requestId));
}

/** Single rack from the overlaid offline list (includes pending creates). */
export async function getOfflineRackById(
  requestId: string,
  rackId: number,
): Promise<RackConfiguration | undefined> {
  return (await getOfflineRackList(requestId)).find(rack => rack.id === rackId);
}
